import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  TextField,
  Typography,
} from '@mui/material';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import dataProvider from '../../providers/dataProvider';
import imageUrl from '../../utils/imageUrl';

const PAGE_SIZE = 10;

/**
 * The value shape an ImageInput expects for an image that already lives in S3.
 * `picture` is what gets saved on the record; `src` is what the preview renders.
 */
export const toImageInputValue = (file) =>
  file && file.fileName
    ? {
        picture: file.fileName,
        src: imageUrl(file.fileName, '100x100'),
        title: file.originalName,
      }
    : null;

const ImagePickerDialog = ({ open, onClose, onSelect }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');

  const totalPages = useMemo(() => (total ? Math.ceil(total / PAGE_SIZE) : 1), [total]);

  const fetchImages = useCallback(async () => {
    if (!open) {
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await dataProvider.getList('file', {
        pagination: { page, perPage: PAGE_SIZE },
        sort: { field: 'createdAt', order: 'DESC' },
        filter: query ? { search: query } : {},
      });
      setImages(response?.data || []);
      setTotal(response?.total || 0);
    } catch (err) {
      setError('Unable to load images. Please try again.');
      setImages([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [open, page, query]);

  useEffect(() => {
    if (!open) {
      return;
    }
    fetchImages();
  }, [open, fetchImages]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setPage(1);
    setQuery(searchInput.trim());
  };

  const handleSelectImage = (image) => {
    onSelect(image);
    onClose();
  };

  const handleExited = () => {
    setImages([]);
    setSearchInput('');
    setQuery('');
    setPage(1);
    setError('');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionProps={{ onExited: handleExited }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Select an Image from Media</DialogTitle>
      <DialogContent>
        <form
          onSubmit={handleSearchSubmit}
          style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}
        >
          <TextField
            fullWidth
            label="Search by file name"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            variant="outlined"
            size="small"
          />
          <Button type="submit" color="primary" variant="contained">
            Search
          </Button>
        </form>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
            <CircularProgress size={24} />
          </div>
        ) : (
          <>
            {error ? (
              <Typography color="error" style={{ marginBottom: 12 }}>
                {error}
              </Typography>
            ) : null}
            {images.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                No images found. Try a different search or upload via Media → Images.
              </Typography>
            ) : (
              <List dense>
                {images.map((image) => (
                  <Fragment key={image.id}>
                    <ListItem button alignItems="center" onClick={() => handleSelectImage(image)}>
                      <img
                        src={imageUrl(image.fileName, '160x90')}
                        alt={image.originalName}
                        style={{
                          width: 64,
                          height: 36,
                          objectFit: 'cover',
                          borderRadius: 4,
                          marginRight: 12,
                          backgroundColor: '#eceff1',
                        }}
                      />
                      <ListItemText
                        primary={image.originalName || image.fileName}
                        secondary={image.fileName}
                      />
                      <ListItemSecondaryAction>
                        <Button
                          color="primary"
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleSelectImage(image);
                          }}
                        >
                          Select
                        </Button>
                      </ListItemSecondaryAction>
                    </ListItem>
                    <Divider component="li" />
                  </Fragment>
                ))}
              </List>
            )}
            {total > PAGE_SIZE ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: 12,
                }}
              >
                <Button disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  Previous
                </Button>
                <Typography variant="body2">
                  Page {page} / {totalPages}
                </Typography>
                <Button
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => (p < totalPages ? p + 1 : p))}
                >
                  Next
                </Button>
              </div>
            ) : null}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Browses images already uploaded to Media → Images and writes the pick into the
 * form field named by `source` (the same field the ImageInput next to it edits),
 * so saving reuses the stored file instead of uploading a new copy.
 *
 * Call sites pass `label={false}` so react-admin's layout components don't add a
 * humanized-source caption above the button; the button text is `buttonLabel`.
 */
const ImagePickerButton = ({
  source = 'picture',
  buttonLabel = 'Browse image library',
  onSelect,
  style,
}) => {
  const [isPickerOpen, setPickerOpen] = useState(false);
  const formContext = useFormContext();

  const handleSelect = useCallback(
    (image) => {
      if (formContext?.setValue) {
        formContext.setValue(source, toImageInputValue(image), {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
      if (onSelect) {
        onSelect(image);
      }
    },
    [formContext, onSelect, source],
  );

  return (
    <Box sx={{ mb: 2 }} style={style}>
      <Button
        type="button"
        variant="outlined"
        size="small"
        startIcon={<PhotoLibraryIcon />}
        onClick={() => setPickerOpen(true)}
      >
        {buttonLabel}
      </Button>
      <ImagePickerDialog
        open={isPickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={handleSelect}
      />
    </Box>
  );
};

export default ImagePickerButton;
